// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { getKnowledgeBase, getKnowledgeBasePages, getKnowledgeBasePageDetail, getClientDocuments, getDocumentContent, getYouTubeVideos, getYouTubeVideoTranscript, deleteDocument, cleanKnowledgeBase, getProducts, getProductCategories, getProductDetail } from '../services/api';
import { getProductCategoryTree } from '../services/api';
import ConfirmationModal from '../components/common/ConfirmationModal';
import { toast } from 'react-toastify';
import { 
  EnvelopeIcon,
  PhoneIcon,
  GlobeAltIcon,
  BookOpenIcon,
  ArrowPathIcon,
  UserGroupIcon,
  CalendarIcon,
  TagIcon,
  LinkIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  BookmarkIcon,
  BuildingLibraryIcon,
  XMarkIcon,
  Bars3Icon,
  ClockIcon,
  FolderIcon,
  DocumentIcon,
  ChevronDownIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import { Node, ShouldCollapseProps } from 'react-d3-tree/lib/types/common';
import Loader from '../components/common/Loader';
import MarkdownRenderer from '../components/common/MarkdownRenderer';

// Updated interface to match the new knowledge base structure
interface KnowledgeBaseData {
    message: string;
    content: {
        pages: Array<{
            url: string;
            domain: string;
            title: string;
            content: string;
            html_content: string;
            last_scraped: string;
        }>;
        contacts: {
            emails: string[];
            phone_numbers: string[];
            social_links: {
                [platform: string]: string[];
            };
            physical_address?: string;
        };
        metadata: {
            total_pages: number;
            domains: string[];
        };
        navigation: string[];
        page_headers: {
            [key: string]: string;
        };
    }
}

// Document interface for uploaded documents
interface DocumentData {
    id: string;
    filename: string;
    file_type: string;
    file_size: number;
    content?: string;
    created_at: string;
    updated_at: string;
    embedding_status?: string;
    pages?: Array<{
        index: number;
        markdown: string;
        images: any[];
        dimensions: {
            dpi: number;
            height: number;
            width: number;
        }
    }>;
}

// Type for the active tab
type ActiveTab = 'website' | 'documents' | 'youtube' | 'products';


const ContactCard = ({ icon, title, children }) => {
    return (
        <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-4">
                <div className="rounded-full p-3 bg-orange-50 text-orange-400">
                    {icon}
                </div>
                <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            </div>
            <div className="space-y-2">
                {children}
            </div>
        </div>
    );
};

const SocialLink = ({ platform, link }) => {
    const displayName = platform.charAt(0).toUpperCase() + platform.slice(1);
    
    return (
        <a 
            href={link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-orange-500 hover:text-orange-600 transition-colors flex items-center gap-2 py-1"
        >
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-orange-50">
                <GlobeAltIcon className="h-3 w-3 text-orange-400" />
            </span>
            {displayName}
        </a>
    );
};

// Format content for proper display
const formatContent = (content) => {
    if (!content) return '';
    
    // If the content is already HTML, just return it
    if (typeof content === 'string' && (
        content.startsWith('<') || 
        content.includes('<p>') || 
        content.includes('<div>') ||
        content.includes('<h1>')
    )) {
        return content;
    }
    
    // Split by double newlines for paragraphs, then format
    return `<p>${content
        .replace(/\n\n+/g, '</p><p>')
        .replace(/\n/g, '<br>')}</p>`;
};

const SectionCard = ({ title, content, icon, isEven = false }) => {
    // More robust content handling
    const contentStr = typeof content === 'string' 
        ? content 
        : (content && typeof content === 'object' && 'toString' in content)
            ? content.toString()
            : '';
    
    // If content is completely empty, show a placeholder message
    const displayContent = contentStr.trim() 
        ? contentStr 
        : 'No content available for this section.';
    
    return (
        <div className={`rounded-xl shadow-sm p-4 hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 ${
            isEven ? 'bg-orange-50' : 'bg-yellow-50'
        }`}>
            <div className="flex items-center gap-3 mb-4">
                <div className={`rounded-full p-3 ${
                    isEven 
                    ? 'bg-orange-100 text-orange-500' 
                    : 'bg-yellow-100 text-yellow-600'
                }`}>
                    {icon || <DocumentTextIcon className="h-5 w-5" />}
                </div>
                <h3 className="text-lg font-medium text-gray-900">{title || 'Untitled'}</h3>
            </div>
            <div className="text-gray-700 prose-sm max-h-40 overflow-hidden relative">
                <div 
                    className="leading-relaxed" 
                    dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(
                            displayContent.substring(0, 250)
                            .replace(/\n\n/g, '</p><p>')
                            .replace(/\n/g, '<br>')
                        )
                    }}
                />
                {displayContent.length > 250 && (
                    <div className={`absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t ${
                        isEven ? 'from-orange-50' : 'from-yellow-50'
                    } to-transparent`} />
                )}
            </div>
        </div>
    );
};

// Helper function to clean up scraped content and ensure proper paragraph formatting
const cleanContent = (content: string): string => {
    if (!content) return '';

    let cleanedContent = content;

    // First, detect and store the navigation block pattern
    // SecureSet specific navigation pattern with markdown links
    const secureSiteNavPattern = /- \[All Products\]\([^)]+\)\s*\n- \[Product Info\]\([^)]+\)\s*\n- \[Secure Set\]\([^)]+\)\s*\n[\s\S]*?- \[Contact\]\([^)]+\)/g;

    // Find all occurrences of the navigation block
    const navMatches = cleanedContent.match(secureSiteNavPattern);

    if (navMatches && navMatches.length > 1) {
        // If the same navigation appears multiple times, keep only the first occurrence
        // and remove all subsequent ones
        const firstNav = navMatches[0];
        navMatches.slice(1).forEach(nav => {
            cleanedContent = cleanedContent.replace(nav, '');
        });

        // Also remove the first occurrence if it appears to be a header nav
        // (typically within the first 1500 chars)
        const firstNavIndex = cleanedContent.indexOf(firstNav);
        if (firstNavIndex >= 0 && firstNavIndex < 1500) {
            cleanedContent = cleanedContent.replace(firstNav, '');
        }
    }

    // Remove other common navigation patterns
    // Remove "Skip to content" links
    cleanedContent = cleanedContent.replace(/Skip to content\s*\n?/gi, '');

    // Remove footer navigation patterns (common footer links)
    const footerPatterns = [
        /- \[Home\]\([^)]+\)\s*\n- \[Fence Post Calculator\]\([^)]+\)[\s\S]*?- \[Refund and Returns Policy\]\([^)]+\)/g,
        /- \[Privacy Policy\]\([^)]+\)\s*\n- \[Terms of Service\]\([^)]+\)[\s\S]*?- \[Contact\]\([^)]+\)/g,
    ];

    footerPatterns.forEach(pattern => {
        cleanedContent = cleanedContent.replace(pattern, '');
    });

    // Remove shopping cart and search UI elements
    cleanedContent = cleanedContent.replace(/No products in the cart\.\s*\n/g, '');
    cleanedContent = cleanedContent.replace(/\$0\.000Cart\s*\n/g, '');
    cleanedContent = cleanedContent.replace(/Search for:Search Button\s*\n/g, '');
    cleanedContent = cleanedContent.replace(/Search for:\s*\n/g, '');
    cleanedContent = cleanedContent.replace(/Search Button\s*\n/g, '');

    // Remove copyright and reCAPTCHA notices
    cleanedContent = cleanedContent.replace(/©\s*\d{4}[\s\S]{0,100}All rights reserved\./gi, '');
    cleanedContent = cleanedContent.replace(/This site is protected by reCAPTCHA[\s\S]{0,200}apply\./gi, '');

    // Remove breadcrumb patterns
    cleanedContent = cleanedContent.replace(/^(Home\s*[›>/]\s*)+[\w\s]+\s*\n/gmi, '');

    // Remove isolated navigation words at the start
    cleanedContent = cleanedContent.replace(/^(Menu|Navigation|Skip to content|Home|About|Products?|Services?|Contact)\s*\n/gmi, '');

    // Remove excessive whitespace but preserve paragraph breaks
    cleanedContent = cleanedContent.replace(/\n{4,}/g, '\n\n\n');  // Max 3 consecutive newlines
    cleanedContent = cleanedContent.replace(/\n\s*\n\s*\n/g, '\n\n'); // Clean up empty lines with spaces

    // Trim leading/trailing whitespace
    cleanedContent = cleanedContent.trim();

    return cleanedContent;
};

// Helper function specifically for cleaning uploaded document content
const cleanDocumentContent = (content: string): string => {
    if (!content) return '';
    
    // Simply return the content as is - the markdown should already be properly formatted from backend
    // The MarkdownRenderer will handle the rendering
    return content;
};


// VideoTranscript Component
const VideoTranscript = ({ video, clientId }) => {
    const [transcript, setTranscript] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchTranscript = async () => {
            try {
                setLoading(true);
                const response = await getYouTubeVideoTranscript(clientId, video.id);
                if (response?.transcript?.segments) {
                    setTranscript(response.transcript);
                }
            } catch (error) {
                console.error('Error fetching transcript:', error);
            } finally {
                setLoading(false);
            }
        };
        
        if (video?.id) {
            fetchTranscript();
        }
    }, [video, clientId]);
    
    if (loading) {
        return (
            <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Transcript</h3>
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2" style={{ borderTopColor: 'rgb(250, 204, 21)', borderBottomColor: 'rgb(250, 204, 21)' }}></div>
                </div>
            </div>
        );
    }
    
    if (!transcript?.segments || transcript.segments.length === 0) {
        return (
            <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Transcript</h3>
                <p className="text-gray-500">No transcript available</p>
            </div>
        );
    }
    
    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        
        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };
    
    return (
        <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Transcript</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                {transcript.segments.map((segment, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                        <span className="text-sm font-medium text-blue-600 whitespace-nowrap">
                            {formatTime(segment.start)}
                        </span>
                        <p className="text-gray-700 flex-1">{segment.text}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

const KnowledgeBase: React.FC = ({ clientId: propClientId }) => {
    const [data, setData] = useState<KnowledgeBaseData | null>(null);
    const [selectedContent, setSelectedContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeHeading, setActiveHeading] = useState("Knowledge Base Overview");
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [clientId, setClientId] = useState(propClientId || localStorage.getItem('client_id'));
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    
    // New states for the tabs
    const [activeTab, setActiveTab] = useState<ActiveTab>('website');
    const [documentsData, setDocumentsData] = useState<DocumentData[]>([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<DocumentData | null>(null);
    const [collapsedPages, setCollapsedPages] = useState<Set<string>>(new Set());
    
    // Function to toggle page collapse state
    const togglePageCollapse = (pageKey: string) => {
        setCollapsedPages(prev => {
            const newSet = new Set(prev);
            if (newSet.has(pageKey)) {
                newSet.delete(pageKey);
            } else {
                newSet.add(pageKey);
            }
            return newSet;
        });
    };
    
    // State for delete confirmation modal
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        documentId: null,
        documentName: ''
    });
    const [isDeleting, setIsDeleting] = useState(false);
    
    // State for YouTube videos
    const [youtubeVideos, setYoutubeVideos] = useState([]);
    const [youtubeLoading, setYoutubeLoading] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    
    // State for clean knowledge base modal
    const [cleanKBModal, setCleanKBModal] = useState({
        isOpen: false
    });
    const [isCleaning, setIsCleaning] = useState(false);


    // Effect to update clientId if localStorage or props change
    useEffect(() => {
        if (!propClientId) {
            const storedClientId = localStorage.getItem('client_id');
            if (storedClientId !== clientId) {
                setClientId(storedClientId);
                console.log('Using client ID from localStorage:', storedClientId);
            }
        } else if (propClientId !== clientId) {
            setClientId(propClientId);
        }
    }, [propClientId, clientId]);

    // Pagination state for website pages, documents, and youtube
    const [websitePages, setWebsitePages] = useState<any[]>([]);
    const [websitePage, setWebsitePage] = useState<number>(1);
    const [websiteTotalPages, setWebsiteTotalPages] = useState<number>(1);
    const [websiteSearch, setWebsiteSearch] = useState<string>('');
    const [websiteLoading, setWebsiteLoading] = useState<boolean>(false);

    const [documentsPage, setDocumentsPage] = useState<number>(1);
    const [documentsTotalPages, setDocumentsTotalPages] = useState<number>(1);
    const [documentsSearch, setDocumentsSearch] = useState<string>('');

    const [youtubePage, setYoutubePage] = useState<number>(1);
    const [youtubeTotalPages, setYoutubeTotalPages] = useState<number>(1);
    const [youtubeSearch, setYoutubeSearch] = useState<string>('');

    // Products state
    const [products, setProducts] = useState<any[]>([]);
    const [productsCategories, setProductsCategories] = useState<any[]>([]);
    const [categoryTree, setCategoryTree] = useState<any[]>([]);
    const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
    const [carouselIndexByUrl, setCarouselIndexByUrl] = useState<{ [url: string]: number }>({});
    const [badImagesByUrl, setBadImagesByUrl] = useState<{ [url: string]: Set<string> }>({});
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [productsPage, setProductsPage] = useState<number>(1);
    const [productsTotalPages, setProductsTotalPages] = useState<number>(1);
    const [productsLoading, setProductsLoading] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<any | null>(null);

    // Fetch lists when tab changes
    useEffect(() => {
        if (!clientId) return;
        if (activeTab === 'website') {
            // Load first page of website pages
            setWebsitePages([]);
            setWebsitePage(1);
            loadWebsitePages(1, websiteSearch, true);
        } else if (activeTab === 'documents') {
            setDocumentsData([]);
            setDocumentsPage(1);
            fetchDocuments(1, documentsSearch, true);
        } else if (activeTab === 'youtube') {
            setYoutubeVideos([]);
            setYoutubePage(1);
            fetchYouTubeVideos(1, youtubeSearch, true);
        } else if (activeTab === 'products') {
            setProducts([]);
            setProductsPage(1);
            fetchProducts(1, selectedCategory);
            fetchProductCategories();
            fetchCategoryTree();
        }
    }, [activeTab, clientId]);

    // Function to fetch website pages (metadata)
    const loadWebsitePages = async (pageNum: number, search: string, replace: boolean = false) => {
        if (!clientId) return;
        try {
            setWebsiteLoading(true);
            const resp = await getKnowledgeBasePages(clientId, pageNum, 12, search || undefined);
            const payload = resp?.data || resp; // handle either shape
            const items = payload?.pages || [];
            const totalPages = payload?.total_pages || 1;
            setWebsiteTotalPages(totalPages);
            setWebsitePage(pageNum);
            setWebsitePages(prev => replace ? items : [...prev, ...items]);
            
            // Auto-select first page if no content is selected and this is the first page
            if (pageNum === 1 && !selectedContent && items.length > 0) {
                try {
                    const firstPage = items[0];
                    if (!firstPage.content || firstPage.content.length === 0) {
                        const detailResp = await getKnowledgeBasePageDetail(clientId, firstPage.url);
                        const full = detailResp?.data || detailResp;
                        setSelectedContent(full);
                    } else {
                        setSelectedContent(firstPage);
                    }
                } catch (e) {
                    console.error('Failed to auto-select first page', e);
                }
            }
        } catch (e) {
            console.error('Error loading website pages', e);
        } finally {
            setWebsiteLoading(false);
        }
    };

    const loadMoreWebsitePages = () => {
        if (websitePage < websiteTotalPages) {
            loadWebsitePages(websitePage + 1, websiteSearch);
        }
    };

    const handleWebsiteSearch = (value: string) => {
        setWebsiteSearch(value);
        loadWebsitePages(1, value, true);
    };

    // Function to fetch documents data (paginated)
    const fetchDocuments = async (pageNum: number = documentsPage, search: string = documentsSearch, replace: boolean = false) => {
        if (!clientId) {
            console.error('No client ID available to fetch documents');
            return;
        }

        try {
            setDocumentsLoading(true);
            const response = await getClientDocuments(clientId, pageNum, 12, search || undefined);
            const payload = response?.data || response;
            const items = payload?.items || [];
            setDocumentsTotalPages(payload?.total_pages || 1);
            setDocumentsPage(pageNum);

            const mapped = items.map((doc: any) => ({
                id: doc.id,
                filename: doc.filename,
                file_type: doc.file_type,
                file_size: doc.file_size,
                created_at: doc.created_at,
                updated_at: doc.updated_at,
                content: '',
                parsedContent: null
            }));

            setDocumentsData(prev => replace ? mapped : [...prev, ...mapped]);
            
            // Auto-select first document if no document is selected and this is the first page
            if (pageNum === 1 && !selectedDocument && mapped.length > 0) {
                // Use handleDocumentClick to properly load content
                handleDocumentClick(mapped[0]);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            setDocumentsData([]);
        } finally {
            setDocumentsLoading(false);
        }
    };

    const loadMoreDocuments = () => {
        if (documentsPage < documentsTotalPages) {
            fetchDocuments(documentsPage + 1, documentsSearch);
        }
    };

    const handleDocumentsSearch = (value: string) => {
        setDocumentsSearch(value);
        fetchDocuments(1, value, true);
    };
    
    // Function to fetch YouTube videos (paginated)
    const fetchYouTubeVideos = async (pageNum: number = youtubePage, search: string = youtubeSearch, replace: boolean = false) => {
        if (!clientId) {
            console.warn('No client ID available for fetching YouTube videos');
            return;
        }
        
        try {
            setYoutubeLoading(true);
            const response = await getYouTubeVideos(clientId, { page: pageNum, limit: 12, search: search || undefined });
            const payload = response?.data || response;
            const items = payload?.items || [];
            setYoutubeTotalPages(payload?.total_pages || 1);
            setYoutubePage(pageNum);
            setYoutubeVideos(prev => replace ? items : [...prev, ...items]);
            
            // Auto-select first video if no video is selected and this is the first page
            if (pageNum === 1 && !selectedVideo && items.length > 0) {
                setSelectedVideo(items[0]);
            }
        } catch (error) {
            console.error('Error fetching YouTube videos:', error);
            setYoutubeVideos([]);
        } finally {
            setYoutubeLoading(false);
        }
    };

    const loadMoreYouTube = () => {
        if (youtubePage < youtubeTotalPages) {
            fetchYouTubeVideos(youtubePage + 1, youtubeSearch);
        }
    };

    const handleYouTubeSearch = (value: string) => {
        setYoutubeSearch(value);
        fetchYouTubeVideos(1, value, true);
    };

    // Products functions
    const fetchProducts = async (pageNum: number = productsPage, category: string | null = selectedCategory) => {
        if (!clientId) {
            console.warn('No client ID available for fetching products');
            return;
        }

        try {
            setProductsLoading(true);
            const response = await getProducts(clientId, category || undefined, pageNum, 50);
            setProducts(response.products || []);
            setProductsTotalPages(response.total_pages || 1);
            setProductsPage(pageNum);

            // Auto-select first product if no product is selected
            if (!selectedProduct && response.products && response.products.length > 0) {
                handleProductClick(response.products[0]);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            setProducts([]);
        } finally {
            setProductsLoading(false);
        }
    };

    // --- Product images helpers ---
    const isPlaceholderImage = (url: string) => {
        if (!url) return true;
        const u = url.toLowerCase().trim();
        if (u.includes('/ui/images/loading.gif')) return true;
        if (u.includes('placeholder')) return true;
        if (u.startsWith('data:image/gif')) return true;
        if (u.endsWith('.gif')) return true; // avoid animated/loading gifs
        return false;
    };

    const markBadImage = (productUrl: string, imgUrl: string) => {
        if (!productUrl || !imgUrl) return;
        setBadImagesByUrl(prev => {
            const next = { ...prev };
            const setForProduct = new Set(next[productUrl] || []);
            setForProduct.add(imgUrl);
            next[productUrl] = setForProduct;
            return next;
        });
    };

    const toProxied = (imageUrl: string) => {
        // Use direct URL for <img>; proxy not needed and can cause extra requests
        return imageUrl || '';
    };

    const nextImage = (productUrl: string, total: number) => {
        setCarouselIndexByUrl(prev => {
            const current = prev[productUrl] || 0;
            const next = (current + 1) % Math.max(total, 1);
            return { ...prev, [productUrl]: next };
        });
    };

    const prevImage = (productUrl: string, total: number) => {
        setCarouselIndexByUrl(prev => {
            const current = prev[productUrl] || 0;
            const next = (current - 1 + Math.max(total, 1)) % Math.max(total, 1);
            return { ...prev, [productUrl]: next };
        });
    };

    const handleImageError = (productUrl: string, currentImg: string, total: number) => {
        // Mark current image bad and advance once; if none left, show fallback
        markBadImage(productUrl, currentImg);
        nextImage(productUrl, total);
    };

    const fetchProductCategories = async () => {
        if (!clientId) return;

        try {
            const response = await getProductCategories(clientId);
            console.log('Product categories response:', response);
            const categories = response.categories || [];
            console.log('Parsed categories:', categories);
            setProductsCategories(categories);
        } catch (error) {
            console.error('Error fetching product categories:', error);
            setProductsCategories([]);
        }
    };

    const fetchCategoryTree = async () => {
        if (!clientId) return;
        try {
            const resp = await getProductCategoryTree(clientId);
            setCategoryTree(resp.tree || []);
        } catch (e) {
            console.error('Error fetching category tree:', e);
            setCategoryTree([]);
        }
    };

    const toggleCategoryPath = (path: string) => {
        setExpandedPaths(prev => {
            const next = new Set(prev);
            if (next.has(path)) next.delete(path); else next.add(path);
            return next;
        });
    };

    const renderCategoryNode = (node: any) => {
        const hasChildren = Array.isArray(node.children) && node.children.length > 0;
        const isExpanded = expandedPaths.has(node.path);
        return (
            <div key={node.path} className="mb-1">
                <button
                    onClick={() => {
                        if (hasChildren) toggleCategoryPath(node.path);
                        setSelectedCategory(node.path);
                        setProductsPage(1);
                        fetchProducts(1, node.path);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors duration-200 ${
                        selectedCategory === node.path
                            ? 'bg-orange-50 text-orange-600 font-semibold'
                            : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {hasChildren && (
                                <span className={`inline-block w-4 text-gray-500 ${isExpanded ? '' : ''}`}>{isExpanded ? '▾' : '▸'}</span>
                            )}
                            <span className="truncate">{node.name}</span>
                        </div>
                        <span className="ml-2 text-xs text-gray-400">{node.count}</span>
                    </div>
                </button>
                {hasChildren && isExpanded && (
                    <div className="ml-4">
                        {node.children.map((child: any) => renderCategoryNode(child))}
                    </div>
                )}
            </div>
        );
    };

    const handleProductClick = async (product: any) => {
        try {
            const detail = await getProductDetail(clientId, product.url || product.id);
            setSelectedProduct(detail);
        } catch (error) {
            console.error('Error fetching product detail:', error);
            setSelectedProduct(product); // Fallback to basic product data
        }
    };

    const handleCategoryClick = (category: string | null) => {
        setSelectedCategory(category);
        setProductsPage(1);
        fetchProducts(1, category);
    };

    // Handle document click - fetch content if not already loaded
    const handleDocumentClick = async (doc: DocumentData) => {
        console.log(`Selected document: ${doc.filename}`);
        
        // If content is already loaded, just set the document
        if (doc.content && doc.content.length > 0) {
            // Check if content is JSON with OCR data
            let processedDoc = { ...doc };
            if (doc.file_type === '.json' || doc.filename.toLowerCase().endsWith('.json')) {
                try {
                    // Try parsing as JSON first to see if it's structured data
                    const jsonContent = JSON.parse(doc.content);
                    // Check if it's Mistral OCR format or similar with pages array
                    if (Array.isArray(jsonContent) && jsonContent.length > 0 && jsonContent[0].pages) {
                        console.log('Detected OCR JSON format with pages, extracting markdown');
                        processedDoc.parsedContent = jsonContent;
                    } else {
                        // If it's JSON but not OCR format, the backend has already extracted markdown
                        // so we'll just use the content as-is (backend already processed it)
                        console.log('JSON document already processed by backend, using extracted content');
                    }
                } catch (e) {
                    // If parsing fails, it means backend already extracted the markdown
                    console.log('Content is already processed markdown from JSON');
                }
            }
            setSelectedDocument(processedDoc);
            return;
        }
        
        // Otherwise, fetch the content
        try {
            const response = await getDocumentContent(doc.id);
            let content = response?.data?.content || response?.content || '';
            
            // Update the document with content
            let updatedDoc = { ...doc, content };
            
            // Check if this is a JSON file with OCR data
            if (doc.file_type === '.json' || doc.filename.toLowerCase().endsWith('.json')) {
                try {
                    // Try parsing as JSON first to see if it's structured data
                    const jsonContent = JSON.parse(content);
                    // Check if it's Mistral OCR format or similar with pages array
                    if (Array.isArray(jsonContent) && jsonContent.length > 0 && jsonContent[0].pages) {
                        console.log('Detected OCR JSON format with pages, extracting markdown');
                        updatedDoc.parsedContent = jsonContent;
                    } else {
                        // If it's JSON but not OCR format, the backend has already extracted markdown
                        console.log('JSON document already processed by backend, using extracted content');
                    }
                } catch (e) {
                    // If parsing fails, it means backend already extracted the markdown
                    console.log('Content is already processed markdown from JSON');
                }
            }
            
            // Update in the documents list
            setDocumentsData(prev => 
                prev.map(d => d.id === doc.id ? updatedDoc : d)
            );
            
            // Set as selected
            setSelectedDocument(updatedDoc);
        } catch (error) {
            console.error('Error fetching document content:', error);
            // Still set the document as selected even if content fetch fails
            setSelectedDocument(doc);
        }
    };

    const handleDeleteClick = (e: React.MouseEvent, doc: DocumentData) => {
        e.stopPropagation(); // Prevent document selection when clicking delete
        setDeleteModal({
            isOpen: true,
            documentId: doc.id,
            documentName: doc.filename
        });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteModal.documentId) return;

        try {
            setIsDeleting(true);
            await deleteDocument(deleteModal.documentId);
            
            // Remove the document from the list
            setDocumentsData(prev => prev.filter(doc => doc.id !== deleteModal.documentId));
            
            // Clear selection if the deleted document was selected
            if (selectedDocument?.id === deleteModal.documentId) {
                setSelectedDocument(null);
            }
            
            // Close modal
            setDeleteModal({ isOpen: false, documentId: null, documentName: '' });
            
            // Show success message
            toast.success('Document deleted successfully');
        } catch (error) {
            console.error('Error deleting document:', error);
            toast.error('Failed to delete document. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModal({ isOpen: false, documentId: null, documentName: '' });
    };

    const handleCleanKnowledgeBase = () => {
        setCleanKBModal({ isOpen: true });
    };

    const handleCleanKBConfirm = async () => {
        if (!clientId) return;

        try {
            setIsCleaning(true);
            const result = await cleanKnowledgeBase(clientId);
            
            // Show detailed success message
            const deleted = result.deleted || {};
            toast.success(
                `Knowledge base cleaned successfully! Deleted: ${deleted.documents || 0} documents, ${deleted.youtube_videos || 0} videos, ${deleted.scraped_pages || 0} pages, ${deleted.embeddings || 0} embeddings`,
                { autoClose: 5000 }
            );
            
            // Refresh the page to show empty state
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } catch (error) {
            console.error('Error cleaning knowledge base:', error);
            toast.error('Failed to clean knowledge base. Please try again.');
        } finally {
            setIsCleaning(false);
            setCleanKBModal({ isOpen: false });
        }
    };

    const handleCleanKBCancel = () => {
        setCleanKBModal({ isOpen: false });
    };

    // Helper function to clean and validate email addresses
    const cleanEmail = (email: string): string | null => {
        if (!email) return null;
        
        // Remove query parameters (everything after ?)
        let cleanedEmail = email.split('?')[0];
        
        // Remove any trailing text that doesn't belong to the email
        // This regex extracts a valid email pattern from the string
        const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;
        const match = cleanedEmail.match(emailRegex);
        
        if (match && match[1]) {
            const validEmail = match[1].toLowerCase();
            
            // Additional validation to ensure it's a proper email
            const fullEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (fullEmailRegex.test(validEmail)) {
                return validEmail;
            }
        }
        
        return null;
    };
    
    // Helper function to clean and format phone numbers
    const cleanPhoneNumber = (phone: string): string | null => {
        if (!phone) return null;
        
        // Remove any HTML tags if present
        let cleaned = phone.replace(/<[^>]*>/g, '');
        
        // Extract phone number pattern - this regex is quite permissive to handle various formats
        const phoneRegex = /[\+\d][\d\s\-\(\)\.]+\d/;
        const match = cleaned.match(phoneRegex);
        
        if (match) {
            // Clean up the matched phone number
            let phoneNumber = match[0]
                .trim()
                .replace(/\s+/g, ' '); // Normalize spaces
            
            // Ensure it has enough digits to be a valid phone number (at least 7 digits)
            const digitCount = phoneNumber.replace(/\D/g, '').length;
            if (digitCount >= 7) {
                return phoneNumber;
            }
        }
        
        return null;
    };

    // Extract unique contacts from all pages
    const uniqueContacts = React.useMemo(() => {
        if (!data?.content?.contacts) return null;
        
        // Clean and deduplicate emails
        const emailSet = new Set<string>();
        const cleanedEmails: string[] = [];
        
        if (data.content.contacts.emails) {
            data.content.contacts.emails.forEach(email => {
                const cleaned = cleanEmail(email);
                if (cleaned && !emailSet.has(cleaned)) {
                    emailSet.add(cleaned);
                    cleanedEmails.push(cleaned);
                }
            });
        }
        
        // Clean and deduplicate phone numbers
        const phoneSet = new Set<string>();
        const cleanedPhones: string[] = [];
        
        if (data.content.contacts.phone_numbers) {
            data.content.contacts.phone_numbers.forEach(phone => {
                // Clean the phone number
                const cleaned = cleanPhoneNumber(phone);
                if (cleaned && !phoneSet.has(cleaned)) {
                    phoneSet.add(cleaned);
                    cleanedPhones.push(cleaned);
                }
            });
        }
        
        return {
            emails: cleanedEmails,
            phone_numbers: cleanedPhones,
            social_links: data.content.contacts.social_links || {},
            hasContacts: (cleanedEmails.length > 0 || 
                         cleanedPhones.length > 0 || 
                         Object.keys(data.content.contacts.social_links || {}).length > 0)
        };
    }, [data]);

    // Helper function to get the best available content for a page
    const getBestContent = (page: any): string => {
        // Priority: main_content > llmfriendlyfullcontent > content > html_content
        if (page.main_content) {
            return page.main_content;
        }
        if (page.llmfriendlyfullcontent?.content) {
            return page.llmfriendlyfullcontent.content;
        }
        if (page.content) {
            return page.content;
        }
        if (page.html_content) {
            return page.html_content;
        }
        return '';
    };

    // Helper function to parse document content by page markers
    const parseDocumentPages = (content: string): { pageNumber: number; content: string }[] => {
        if (!content) return [];
        
        console.log('Parsing document content, length:', content.length);
        console.log('First 200 chars:', content.substring(0, 200));
        
        // Look for different page marker patterns
        const pagePatterns = [
            /(?:^|\n)##\s*Page\s*(\d+)\s*\n/gm,  // ## Page X
            /(?:^|\n)Page\s*(\d+)\s*\n/gm,       // Page X
            /\[Page\s*(\d+)\]/gm,                // [Page X]
            /---\s*\n##\s*Page\s*(\d+)/gm        // --- ## Page X
        ];
        
        let matches: RegExpMatchArray[] = [];
        let usedPattern: RegExp | null = null;
        
        // Try each pattern to find which one matches
        for (const pattern of pagePatterns) {
            const testMatches = Array.from(content.matchAll(pattern));
            if (testMatches.length > 0) {
                matches = testMatches;
                usedPattern = pattern;
                console.log('Found matches with pattern:', pattern.source, 'Count:', testMatches.length);
                break;
            }
        }
        
        if (matches.length === 0) {
            console.log('No page markers found, treating as single page');
            // No page markers found, treat entire content as one page
            return [{ pageNumber: 1, content: content.trim() }];
        }
        
        const pages: { pageNumber: number; content: string }[] = [];
        
        // Check if there's content before the first page marker
        const firstPageIndex = matches[0].index!;
        if (firstPageIndex > 50) { // If there's substantial content before first marker
            const beforeFirstPage = content.slice(0, firstPageIndex).trim();
            if (beforeFirstPage && !beforeFirstPage.includes('Document Information')) {
                pages.push({ pageNumber: 1, content: beforeFirstPage });
            }
        }
        
        // Process matched pages
        for (let i = 0; i < matches.length; i++) {
            const match = matches[i];
            const pageNumber = parseInt(match[1]);
            
            const startIndex = match.index! + match[0].length;
            const endIndex = i < matches.length - 1 ? matches[i + 1].index : content.length;
            
            let pageContent = content.slice(startIndex, endIndex).trim();
            
            // Remove trailing page separators
            pageContent = pageContent.replace(/---\s*$/, '').trim();
            
            if (pageContent) {
                pages.push({ pageNumber, content: pageContent });
            }
        }
        
        console.log('Parsed pages:', pages.length);
        return pages.sort((a, b) => a.pageNumber - b.pageNumber);
    };


    // Helper function to check if a page has meaningful content
    const hasValidContent = (page: any): boolean => {
        const title = page.title?.trim();
        if (!title) {
            return false;
        }

        const normalizedTitle = title.toLowerCase();
        if (['untitled', 'no title'].includes(normalizedTitle)) {
            return false;
        }
        
        const content = getBestContent(page);
        if (!content) {
            return false;
        }
        
        const cleanedContent = cleanContent(content);
        if (!cleanedContent) {
            return false;
        }

        const wordCount = cleanedContent.split(/\s+/).filter(Boolean).length;
        const charCount = cleanedContent.replace(/\s+/g, ' ').trim().length;

        if (wordCount < 20 && charCount < 120) {
            return false;
        }
        
        return true;
    };

    // Create a pages-like structure for backwards compatibility
    const pages = React.useMemo(() => {
        if (!data) return [];
        
        // Handle potentially empty response or missing keys
        if (!data.content) {
            console.warn('Knowledge base data has no content field:', data);
            return [{
                title: 'No content available',
                content: 'No content has been scraped for this website yet.',
                domain: '',
                url: '',
            }];
        }
        
        let allPages = [];
        
        // If pages is already an array, use it directly
        if (data.content.pages && Array.isArray(data.content.pages)) {
            allPages = data.content.pages;
        }
        // If pages is an object, transform it to an array
        else if (data.content.pages && typeof data.content.pages === 'object') {
            allPages = Object.entries(data.content.pages).map(([url, pageData]) => ({
                url,
                title: pageData.title || 'Untitled',
                content: pageData.content || '',
                html_content: pageData.html_content || '',
                domain: pageData.domain || new URL(url).hostname,
                meta_description: pageData.meta_description || '',
                last_scraped: pageData.last_scraped || new Date().toISOString(),
                headings: pageData.headings || [],
                main_content: pageData.main_content || pageData.content || '',
                last_updated: pageData.last_scraped || new Date().toISOString(),
                author: pageData.author || '',
                categories: pageData.categories || [],
                original_headings: pageData.original_headings || [],
                table_of_contents: pageData.table_of_contents || []
            }));
        }
        
        // Filter out pages without valid content
        const validPages = allPages.filter(hasValidContent);
        
        // Remove duplicates based on URL and title
        const uniquePages = validPages.reduce((acc, page) => {
            // Check if we already have a page with this URL OR same title
            const existingPageIndex = acc.findIndex(p => 
                (p.url && page.url && p.url === page.url) || 
                (p.title && page.title && p.title === page.title)
            );
            
            if (existingPageIndex === -1) {
                // No duplicate, add the page
                acc.push(page);
            } else {
                // Duplicate found, keep the one with more content or better title
                const existingPage = acc[existingPageIndex];
                const existingContentLength = getBestContent(existingPage).length;
                const newContentLength = getBestContent(page).length;
                
                // Replace if new page has more content
                if (newContentLength > existingContentLength) {
                    acc[existingPageIndex] = page;
                }
            }
            
            return acc;
        }, []);
        
        console.log('Pages deduplication:', {
            before: validPages.length,
            after: uniquePages.length,
            duplicatesRemoved: validPages.length - uniquePages.length
        });
        
        // If no valid pages found, return empty array
        return uniquePages;
    }, [data]);

    // Filter pages based on active heading
    const filteredPages = React.useMemo(() => {
        if (!pages.length || !activeHeading) {
            return pages;
        }
        
        // Case-insensitive filtering to catch more matches
        const lowercaseHeading = activeHeading.toLowerCase();
        
        return pages.filter(page => {
            // Check if heading is in page headings array
            const headingMatch = page.headings?.some(heading => {
                const headingText = typeof heading === 'string' ? heading : heading.text || '';
                return headingText.toLowerCase().includes(lowercaseHeading);
            });
            
            // Check if heading is in page title
            const titleMatch = page.title?.toLowerCase().includes(lowercaseHeading);
            
            // Check if heading is in page content
            const contentMatch = page.content?.toLowerCase().includes(lowercaseHeading);
            
            return headingMatch || titleMatch || contentMatch;
        });
    }, [pages, activeHeading]);

    // Get sections for sidebar navigation
    const sections = React.useMemo(() => {
        if (!pages || pages.length === 0) return {};
        
        // Create sections from headings
        const result = {};
        
        // Collect all unique headings from pages
        const allHeadings = new Set();
        
        pages.forEach(page => {
            // If page has original_headings (with level information), use that
            if (page.original_headings && page.original_headings.length > 0) {
                page.original_headings.forEach(heading => {
                    if (heading.text) {
                        allHeadings.add(heading.text);
                        
                        // Create entry if it doesn't exist
                        if (!result[heading.text]) {
                            result[heading.text] = [];
                        }
                        
                        // Add page to this heading if not already there
                        const pageEntry = {
                            url: page.url,
                            title: page.title,
                            domain: page.domain
                        };
                        
                        // Check if page is already in the array
                        if (!result[heading.text].some(p => p.url === page.url)) {
                            result[heading.text].push(pageEntry);
                        }
                    }
                });
            } 
            // Fallback to regular headings array
            else if (page.headings && page.headings.length > 0) {
                page.headings.forEach(heading => {
                    if (heading) {
                        const headingText = typeof heading === 'string' ? heading : heading.text || '';
                        if (headingText) {
                            allHeadings.add(headingText);
                            
                            // Create entry if it doesn't exist
                            if (!result[headingText]) {
                                result[headingText] = [];
                            }
                            
                            // Add page to this heading
                            const pageEntry = {
                                url: page.url,
                                title: page.title,
                                domain: page.domain
                            };
                            
                            // Check if page is already in the array
                            if (!result[headingText].some(p => p.url === page.url)) {
                                result[headingText].push(pageEntry);
                            }
                        }
                    }
                });
            }
        });
        
        // If we didn't find any headings, use page titles as sections
        if (allHeadings.size === 0) {
            pages.forEach(page => {
                const title = page.title || 'Untitled Page';
                result[title] = [{
                    url: page.url,
                    title: page.title,
                    domain: page.domain
                }];
            });
        }
        
        return result;
    }, [pages]);

    // Page headers for navigation
    const pageHeaders = React.useMemo(() => {
        return data?.headings || [];
    }, [data]);
    
    const handleHeadingClick = (heading) => {
        setActiveHeading(heading);
        // Reset selected content when changing heading
        setSelectedContent(null);
    };

    // Handle the section click
    const handleSectionClick = (section) => {
        setActiveHeading(section);
        // Auto select the first content from this section
        if (sections[section] && sections[section].length > 0) {
            // Find the matching page for this section
            const pageForSection = pages.find(
                page => page.url === sections[section][0].url
            );
            
            if (pageForSection) {
                setSelectedContent(pageForSection);
            }
        }
    };

    // Handle click on a page card
    const handlePageClick = (page) => {
        console.log("Page clicked:", page);
        setSelectedContent(page);
    };


    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // REMOVED: Don't fetch all knowledge base content at once
                // This was causing performance issues with large datasets
                // We'll rely on paginated loading instead

                // Just set empty data structure - content will be loaded via pagination
                const transformedData = {
                    message: "Content will be loaded on demand",
                    content: {
                        pages: [],
                        contacts: {
                            emails: [],
                            phone_numbers: [],
                            social_links: {},
                            physical_address: null
                        },
                        metadata: {
                            total_pages: 0,
                            domains: []
                        },
                        navigation: [],
                        page_headers: {}
                    }
                };

                setData(transformedData);
                // Don't set any active heading - will be set when pages are loaded
            } catch (error) {
                console.error('Error fetching knowledge base:', error);
                // Set a default structure for error state
                setData({ 
                    message: "Error loading content", 
                    content: {
                        pages: [],
                        contacts: {
                            emails: [],
                            phone_numbers: [],
                            social_links: {},
                            physical_address: null
                        },
                        metadata: {
                            total_pages: 0,
                            domains: []
                        },
                        navigation: [],
                        page_headers: {}
                    }
                });
            } finally {
                setLoading(false);
            }
        };

        if (clientId) {
            fetchData();
        } else {
            setLoading(false);
            console.error('No client ID available to fetch knowledge base');
            setData({ 
                message: "No client ID", 
                content: {
                    pages: [],
                    contacts: {
                        emails: [],
                        phone_numbers: [],
                        social_links: {},
                        physical_address: null
                    },
                    metadata: {
                        total_pages: 0,
                        domains: []
                    },
                    navigation: [],
                    page_headers: {}
                }
            });
        }
    }, [clientId]);

    // Get an appropriate icon for a heading based on its content
    const getHeadingIcon = (heading) => {
        // Default icon if heading is null or undefined
        if (!heading) {
            return <BookOpenIcon className="h-5 w-5" />;
        }
        
        const lowercaseHeading = heading.toLowerCase();
        
        if (lowercaseHeading.includes('about')) {
            return <UserGroupIcon className="h-5 w-5" />;
        } else if (lowercaseHeading.includes('testimonial') || lowercaseHeading.includes('review')) {
            return <ChatBubbleLeftRightIcon className="h-5 w-5" />;
        } else if (lowercaseHeading.includes('event') || lowercaseHeading.includes('calendar')) {
            return <CalendarIcon className="h-5 w-5" />;
        } else if (lowercaseHeading.includes('service') || lowercaseHeading.includes('product')) {
            return <TagIcon className="h-5 w-5" />;
        } else if (lowercaseHeading.includes('resource') || lowercaseHeading.includes('download')) {
            return <LinkIcon className="h-5 w-5" />;
        } else if (lowercaseHeading.includes('program') || lowercaseHeading.includes('framework')) {
            return <ArrowPathIcon className="h-5 w-5" />;
        } else {
            return <BookOpenIcon className="h-5 w-5" />;
        }
    };

    // Get an appropriate icon for a section
    const getSectionIcon = (section) => {
        return getHeadingIcon(section);
    };

    // Get icon for a page based on its content
    const getPageIcon = (page) => {
        if (page.title) {
            return getHeadingIcon(page.title);
        } else if (page.headings && page.headings.length > 0) {
            return getHeadingIcon(page.headings[0]);
        } else {
            return <DocumentTextIcon className="h-5 w-5" />;
        }
    };

    // REMOVED: Auto-select logic moved to loadWebsitePages to prevent duplicate calls
    // This was causing multiple API calls to getKnowledgeBasePageDetail

    // Render contact information from the unique contacts
    const renderContactSection = () => {
        if (!uniqueContacts || !uniqueContacts.hasContacts) {
            return null;
        }

        return (
            <div className="mb-8 max-w-7xl mx-auto">
                <h2 className="text-xl font-semibold mb-6 text-left text-black">
                    Contact Information
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Email Block */}
                    {uniqueContacts.emails.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm p-6 transition-all duration-300 hover:shadow-md w-full">
                            <div className="flex items-center gap-3 mb-4">
                                <EnvelopeIcon className="h-5 w-5" style={{ color: 'rgb(250, 204, 21)' }} />
                                <h3 className="text-lg font-medium text-gray-900">Email</h3>
                            </div>
                            <div className="space-y-2 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
                                {uniqueContacts.emails.map((email, index) => (
                                    <a 
                                        key={index}
                                        href={`mailto:${email}`}
                                        className="text-black hover:opacity-80 transition-colors flex items-center gap-2 block"
                                    >
                                        <span>{email}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Phone Block */}
                    {uniqueContacts.phone_numbers.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm p-6 transition-all duration-300 hover:shadow-md w-full">
                            <div className="flex items-center gap-3 mb-4">
                                <PhoneIcon className="h-5 w-5" style={{ color: 'rgb(250, 204, 21)' }} />
                                <h3 className="text-lg font-medium text-gray-900">Phone</h3>
                            </div>
                            <div className="space-y-2 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
                                {uniqueContacts.phone_numbers.map((phone, index) => (
                                    <a 
                                        key={index}
                                        href={`tel:${phone}`}
                                        className="text-black hover:opacity-80 transition-colors flex items-center gap-2 block"
                                    >
                                        <span>{phone}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Social Media Block */}
                    {Object.keys(uniqueContacts.social_links).length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm p-6 transition-all duration-300 hover:shadow-md w-full">
                            <div className="flex items-center gap-3 mb-4">
                                <GlobeAltIcon className="h-5 w-5" style={{ color: 'rgb(250, 204, 21)' }} />
                                <h3 className="text-lg font-medium text-gray-900">Social Media</h3>
                            </div>
                            <div className="space-y-2 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
                                {Object.entries(uniqueContacts.social_links).map(([platform, links]) => (
                                    links.map((link, linkIndex) => (
                                        <a 
                                            key={`${platform}-${linkIndex}`}
                                            href={link} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="text-black hover:opacity-80 transition-colors flex items-center gap-2 block"
                                        >
                                            {platform.charAt(0).toUpperCase() + platform.slice(1)}
                                        </a>
                                    ))
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Render the selected content or a message to select content
    const renderContentArea = () => {
        if (selectedContent) {
            return (
                <div className="">
                    <div>
                        {/* Title */}
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedContent.title}</h2>
                        
                        {/* Meta Description */}
                        {selectedContent.meta_description && (
                            <p className="text-gray-600 mb-6">{selectedContent.meta_description}</p>
                        )}
                        
                        {/* Domain, Last Updated, and Source Link */}
                        <div className="flex flex-wrap gap-4 mb-6">
                            {selectedContent.domain && (
                                <div className="flex items-center gap-1 text-sm text-gray-500">
                                    <GlobeAltIcon className="h-4 w-4" style={{ color: 'rgb(250, 204, 21)' }} />
                                    <span>{selectedContent.domain}</span>
                                </div>
                            )}
                            
                            {selectedContent.last_scraped && (
                                <div className="flex items-center gap-1 text-sm text-gray-500">
                                    <ClockIcon className="h-4 w-4" style={{ color: 'rgb(250, 204, 21)' }} />
                                    <span>Last updated: {new Date(selectedContent.last_scraped).toLocaleDateString()}</span>
                                </div>
                            )}
                            
                            {selectedContent.url && (
                                <a 
                                    href={selectedContent.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-sm transition-colors hover:opacity-80"
                                    style={{ color: 'rgb(250, 204, 21)' }}
                                >
                                    <LinkIcon className="h-4 w-4" />
                                    <span>View Original Source</span>
                                </a>
                            )}
                        </div>
                        
                        {/* Page Content - Show full website page content */}
                        {getBestContent(selectedContent) && (
                            <div className="">
                                <div className="">
                                    <MarkdownRenderer 
                                        content={cleanContent(getBestContent(selectedContent))}
                                        colorScheme="yellow"
                                        baseUrl={selectedContent.domain}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        
        const websiteList = websitePages.length > 0 ? websitePages : pages;

        if (websiteLoading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <Loader 
                        text="Loading page content..." 
                        variant="pulse"
                        size="medium"
                    />
                </div>
            );
        }

        if (websiteList.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center h-64 text-center text-gray-500 space-y-2">
                    <BookOpenIcon className="h-10 w-10 text-yellow-400" />
                    <p className="font-medium text-gray-700">No website pages found yet</p>
                    <p className="text-sm text-gray-500 max-w-sm">
                        Run a crawl to populate your knowledge base, or check back once the current crawl finishes.
                    </p>
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center justify-center h-64 text-gray-600 space-y-2">
                <DocumentTextIcon className="h-10 w-10 text-yellow-400" />
                <p className="font-medium text-gray-700">Select a page to view its details</p>
                <p className="text-sm text-gray-500 max-w-sm text-center">
                    Choose any page from the list on the left to preview its content here.
                </p>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="h-full min-h-[600px] relative">
                <Loader 
                    fullPage={false} 
                    text="Loading knowledge base..." 
                    variant="pulse"
                    overlay={true} 
                />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="max-w-screen-xl mx-auto p-4 md:p-6 bg-white">
                <h1 className="text-3xl font-bold mb-6 text-gray-900 pb-3 border-b border-gray-200">Knowledge Base</h1>
                <div className="bg-white rounded-xl shadow-sm p-6 text-center">
                    <div className="text-orange-400 mb-3">
                        <BookOpenIcon className="h-12 w-12 mx-auto" />
                    </div>
                    <p className="text-gray-500 text-lg">No data available. Please try again later.</p>
                </div>
            </div>
        );
    }

    // Tab button component
    const TabButton = ({ tab, label, icon }) => (
        <button
            onClick={() => setActiveTab(tab)}
            className={`py-3 pr-6 pl-0 text-sm font-medium transition-colors duration-200 flex items-center space-x-2
                ${activeTab === tab 
                    ? 'font-semibold' 
                    : 'text-gray-600 hover:text-gray-800'}`}
            style={activeTab === tab ? { color: 'rgb(250, 204, 21)' } : {}}
        >
            {icon}
            <span>{label}</span>
        </button>
    );

    return (
        <div className="min-h-screen pb-12">
            {loading ? (
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500 mx-auto mb-4"></div>
                        <p className="text-lg text-gray-600">Loading knowledge base...</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Contact Section at the top */}
                    {renderContactSection()}
                    
                    {/* Tabs for switching between Website and Documents */}
                    <div className="max-w-7xl mx-auto mb-6">
                        <div className="flex justify-between items-center">
                            <div className="flex justify-start">
                                <TabButton 
                                    tab="website" 
                                    label="Website Content" 
                                    icon={<GlobeAltIcon className="h-5 w-5" />}
                                />
                                <TabButton 
                                    tab="documents" 
                                    label="Uploaded Documents" 
                                    icon={<DocumentIcon className="h-5 w-5" />}
                                />
                                <TabButton
                                    tab="youtube"
                                    label="YouTube Videos"
                                    icon={<svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                    </svg>}
                                />
                                <TabButton
                                    tab="products"
                                    label="Products"
                                    icon={<TagIcon className="h-5 w-5" />}
                                />
                            </div>
                            <button
                                onClick={handleCleanKnowledgeBase}
                                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors flex items-center gap-2"
                            >
                                <TrashIcon className="h-4 w-4" />
                                Clean Knowledge Base
                            </button>
                        </div>
                    </div>
                    
                    {/* Conditional rendering based on active tab */}
                    {activeTab === 'website' && (() => {
                        const websiteList = websitePages.length > 0 ? websitePages : pages;

                        if (websiteLoading && websiteList.length === 0) {
                            return (
                                <div className="flex items-center justify-center max-w-4xl mx-auto min-h-[300px]">
                                    <Loader 
                                        text="Loading website pages..." 
                                        variant="pulse"
                                        size="large"
                                    />
                                </div>
                            );
                        }

                        if (!websiteLoading && websiteList.length === 0) {
                            return (
                                <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-10 text-center">
                                    <div className="flex flex-col items-center space-y-4">
                                        <BookOpenIcon className="h-12 w-12 text-yellow-400" />
                                        <h3 className="text-xl font-semibold text-gray-900">No website content yet</h3>
                                        <p className="text-gray-600 max-w-md">
                                            We haven&apos;t discovered any crawled pages for this site. Once a crawl finishes,
                                            your website content will appear here automatically.
                                        </p>
                                    </div>
                                </div>
                            );
                        }

                        return (
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 max-w-7xl mx-auto relative">
                            {/* Mobile Menu Toggle Button - only visible on small screens */}
                            <div className="lg:hidden sticky top-4 z-20 mb-4">
                                <button 
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    className="flex items-center justify-center gap-2 w-full bg-white rounded-lg shadow-sm p-3 border border-orange-100"
                                >
                                    {mobileMenuOpen ? (
                                        <>
                                            <XMarkIcon className="h-5 w-5 text-orange-500" />
                                            <span className="font-medium text-gray-800">Close Navigation</span>
                                        </>
                                    ) : (
                                        <>
                                            <Bars3Icon className="h-5 w-5 text-orange-500" />
                                            <span className="font-medium text-gray-800">Browse Pages</span>
                                        </>
                                    )}
                                </button>
                            </div>
                            
                            {/* Sidebar - hidden on mobile unless menu is open */}
                            <div className={`lg:col-span-1 ${mobileMenuOpen ? 'block' : 'hidden lg:block'} lg:sticky lg:top-6 lg:self-start transition-all duration-300`}>
                                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                                    <div className="p-4 bg-white">
                                        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                            <BookOpenIcon className="h-5 w-5" style={{ color: 'rgb(250, 204, 21)' }} />
                                            Knowledge Base
                                        </h2>
                                    </div>
                                    
                                    <div className="p-4">
                                        <div>
                                            <h3 className="text-sm font-medium uppercase tracking-wider mb-3" style={{ color: 'rgb(250, 204, 21)' }}>
                                                Pages ({websiteList.length})
                                            </h3>
                                            <nav className="space-y-1 max-h-[calc(100vh-260px)] overflow-y-auto pr-2 custom-scrollbar">
                                                {websiteList.map((page, index) => {
                                                    const hasTableOfContents = (page.table_of_contents && page.table_of_contents.length > 0) || 
                                                                              (page.original_headings && page.original_headings.length > 0);
                                                    const pageId = page.url || `page-${index}`;
                                                    // Create a unique key combining URL and title to avoid React warnings
                                                    const uniqueKey = `${page.url || ''}-${page.title || ''}-${index}`;
                                                    
                                                    return (
                                                        <div key={uniqueKey} className="relative">
                                                            <div 
                                                                className={`flex items-center justify-between px-3 py-2 text-sm rounded-md cursor-pointer transition-colors duration-200 ${
                                                                    selectedContent?.url === page.url
                                                                    ? 'bg-yellow-50 text-black font-semibold'
                                                                    : 'text-gray-700 hover:text-gray-800'
                                                                }`}
                                                            >
                                                                <div 
                                                                    onClick={async () => {
                                                                        try {
                                                                            // If this is a metadata-only page (no content), fetch detail
                                                                            if (!page.content || page.content.length === 0) {
                                                                                const detailResp = await getKnowledgeBasePageDetail(clientId, page.url);
                                                                                const full = detailResp?.data || detailResp;
                                                                                setSelectedContent(full);
                                                                            } else {
                                                                                setSelectedContent(page);
                                                                            }
                                                                        } catch (e) {
                                                                            console.error('Failed to load page detail', e);
                                                                            setSelectedContent(page);
                                                                        }
                                                                        setMobileMenuOpen(false);
                                                                        setOpenDropdown(null);
                                                                    }}
                                                                    className="flex items-center flex-1"
                                                                >
                                                                    <DocumentTextIcon 
                                                                        className={`mr-3 flex-shrink-0 h-5 w-5 ${
                                                                            selectedContent?.url === page.url ? 'text-black' : ''
                                                                        }`}
                                                                        style={{ color: selectedContent?.url === page.url ? 'black' : 'rgb(250, 204, 21)' }}
                                                                    />
                                                                    <div className="flex-1 min-w-0">
                                                                        <span className="truncate block">{page.title || 'Untitled'}</span>
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Dropdown Toggle Button */}
                                                                {hasTableOfContents && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setOpenDropdown(openDropdown === pageId ? null : pageId);
                                                                        }}
                                                                        className="ml-2 p-1 rounded hover:bg-yellow-100 transition-colors duration-200"
                                                                    >
                                                                        <ChevronDownIcon 
                                                                            className={`h-4 w-4 transition-transform duration-200 ${
                                                                                openDropdown === pageId ? 'transform rotate-180' : ''
                                                                            } ${selectedContent?.url === page.url ? 'text-black' : ''}`}
                                                                            style={{ color: selectedContent?.url === page.url ? 'black' : 'rgb(250, 204, 21)' }}
                                                                        />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            
                                                            {/* Dropdown Menu */}
                                                            {hasTableOfContents && openDropdown === pageId && (
                                                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-yellow-200 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                                                                    <div className="p-2">
                                                                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2 px-2">
                                                                            Table of Contents
                                                                        </div>
                                                                        
                                                                        {/* Use table_of_contents if available */}
                                                                        {page.table_of_contents && page.table_of_contents.length > 0 ? (
                                                                            page.table_of_contents.map((tocItem, tocIndex) => (
                                                                                <div
                                                                                    key={tocIndex}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setSelectedContent(page); // Ensure page is selected
                                                                                        setOpenDropdown(null); // Close dropdown
                                                                                        setTimeout(() => {
                                                                                            const element = document.getElementById(`toc-${tocIndex}`);
                                                                                            if (element) {
                                                                                                element.scrollIntoView({ behavior: 'smooth' });
                                                                                            }
                                                                                        }, 100);
                                                                                    }}
                                                                                    className="flex items-center px-2 py-2 text-xs rounded cursor-pointer hover:bg-yellow-50 transition-colors duration-200"
                                                                                    style={{ 
                                                                                        paddingLeft: `${Math.max(0.5, (tocItem.level || 1) * 0.5)}rem`,
                                                                                        color: 'rgb(75, 85, 99)'
                                                                                    }}
                                                                                >
                                                                                    <span className="truncate hover:text-yellow-600">
                                                                                        {tocItem.title || tocItem.text || tocItem}
                                                                                    </span>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            /* Fallback to original_headings */
                                                                            page.original_headings && page.original_headings.slice(0, 10).map((heading, headingIndex) => (
                                                                                <div
                                                                                    key={headingIndex}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setSelectedContent(page); // Ensure page is selected
                                                                                        setOpenDropdown(null); // Close dropdown
                                                                                        setTimeout(() => {
                                                                                            const element = document.getElementById(`heading-${headingIndex}`);
                                                                                            if (element) {
                                                                                                element.scrollIntoView({ behavior: 'smooth' });
                                                                                            }
                                                                                        }, 100);
                                                                                    }}
                                                                                    className="flex items-center px-2 py-2 text-xs rounded cursor-pointer hover:bg-yellow-50 transition-colors duration-200"
                                                                                    style={{ 
                                                                                        paddingLeft: `${Math.max(0.5, (heading.level || 1) * 0.5)}rem`,
                                                                                        color: 'rgb(75, 85, 99)'
                                                                                    }}
                                                                                >
                                                                                    <span className="truncate hover:text-yellow-600">
                                                                                        {heading.text || heading}
                                                                                    </span>
                                                                                </div>
                                                                            ))
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </nav>
                                            {/* Load more for website pages - outside scroll */}
                                            {websiteTotalPages > websitePage && (
                                                <div className="mt-3 flex justify-center">
                                                    <button
                                                        onClick={loadMoreWebsitePages}
                                                        disabled={websiteLoading}
                                                        className="px-3 py-2 text-sm rounded bg-yellow-100 text-yellow-800 hover:bg-yellow-200 disabled:opacity-60"
                                                    >
                                                        {websiteLoading ? 'Loading...' : 'Load more'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Main content area */}
                            <div className="lg:col-span-3 mb-20 lg:mb-0">
                                <div className="bg-white rounded-xl shadow-sm p-6 min-h-[calc(100vh-200px)]">
                                    {/* Removed Back to Overview button since we auto-select first page */}
                                    {renderContentArea()}
                                </div>
                            </div>
                        </div>
                        );
                    })()}

                    {activeTab === 'documents' && (
                        /* Documents Tab */
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 max-w-7xl mx-auto relative">
                            {/* Documents Sidebar */}
                            <div className="lg:col-span-1 lg:sticky lg:top-6 lg:self-start">
                                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                                    <div className="p-4 bg-white">
                                        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                            <FolderIcon className="h-5 w-5" style={{ color: 'rgb(250, 204, 21)' }} />
                                            Documents
                                        </h2>
                                    </div>
                                    
                                    <div className="p-4">
                                        {documentsLoading ? (
                                            <div className="py-20 flex justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2" style={{ borderTopColor: 'rgb(250, 204, 21)', borderBottomColor: 'rgb(250, 204, 21)' }}></div>
                                            </div>
                                        ) : documentsData.length === 0 ? (
                                            <div className="text-center py-8">
                                                <DocumentIcon className="h-12 w-12 mx-auto mb-3" style={{ color: 'rgb(250, 204, 21)' }} />
                                                <p className="text-gray-500">No documents available</p>
                                            </div>
                                        ) : (
                                            <>
                                            <nav className="space-y-1 max-h-[calc(100vh-260px)] overflow-y-auto pr-2 custom-scrollbar">
                                                {documentsData.map((doc) => (
                                                    <div 
                                                        key={doc.id}
                                                        className={`group relative rounded-md transition-all duration-200 ${
                                                            selectedDocument?.id === doc.id
                                                            ? 'bg-yellow-50'
                                                            : 'hover:bg-gray-50'
                                                        } mb-2`}
                                                    >
                                                        <div className="flex items-start p-3">
                                                            <div 
                                                                onClick={async () => {
                                                                    // Fetch full document content on demand
                                                                    try {
                                                                        const resp = await getDocumentContent(doc.id);
                                                                        const payload = resp?.data || resp;
                                                                        const fullDoc = {
                                                                            ...doc,
                                                                            content: payload?.content || payload?.data?.content || '',
                                                                        };
                                                                        handleDocumentClick(fullDoc as any);
                                                                    } catch (e) {
                                                                        console.error('Failed to load document content', e);
                                                                        handleDocumentClick(doc as any);
                                                                    }
                                                                }}
                                                                className="flex-1 cursor-pointer min-w-0"
                                                            >
                                                                <p className={`text-sm font-medium leading-tight break-words ${
                                                                    selectedDocument?.id === doc.id 
                                                                    ? 'text-black' 
                                                                    : 'text-gray-700'
                                                                }`}
                                                                title={doc.filename}
                                                                >
                                                                    {doc.filename}
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={(e) => handleDeleteClick(e, doc)}
                                                                className="ml-2 p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-200 flex-shrink-0"
                                                                title="Delete document"
                                                            >
                                                                <TrashIcon className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </nav>
                                            {/* Load more for documents - outside scroll */}
                                            {documentsTotalPages > documentsPage && (
                                                <div className="mt-3 flex justify-center">
                                                    <button
                                                        onClick={loadMoreDocuments}
                                                        disabled={documentsLoading}
                                                        className="px-3 py-2 text-sm rounded bg-yellow-100 text-yellow-800 hover:bg-yellow-200 disabled:opacity-60"
                                                    >
                                                        {documentsLoading ? 'Loading...' : 'Load more'}
                                                    </button>
                                                </div>
                                            )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Documents Content */}
                            <div className="lg:col-span-3 mb-20 lg:mb-0">
                                <div className="bg-white rounded-xl shadow-sm p-6 min-h-[calc(100vh-200px)]">
                                    {documentsLoading ? (
                                        <div className="flex justify-center items-center h-64">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderTopColor: 'rgb(250, 204, 21)', borderBottomColor: 'rgb(250, 204, 21)' }}></div>
                                        </div>
                                    ) : selectedDocument ? (
                                        <div>
                                            
                                            <div>
                                                {/* Document Title */}
                                                <h2 className="text-2xl font-bold text-gray-900 mb-4">{selectedDocument.filename}</h2>
                                                
                                                {/* Document Meta Info */}
                                                <div className="flex flex-wrap gap-4 mb-6">
                                                    <div className="flex items-center gap-1 text-sm text-gray-500">
                                                        <DocumentIcon className="h-4 w-4" style={{ color: 'rgb(250, 204, 21)' }} />
                                                        <span>{selectedDocument.file_type.replace('.', '').toUpperCase()}</span>
                                                    </div>
                                                    
                                                    <div className="flex items-center gap-1 text-sm text-gray-500">
                                                        <ClockIcon className="h-4 w-4" style={{ color: 'rgb(250, 204, 21)' }} />
                                                        <span>Uploaded: {new Date(selectedDocument.created_at).toLocaleDateString()}</span>
                                                    </div>
                                                    
                                                    <div className="flex items-center gap-1 text-sm text-gray-500">
                                                        <span>{parseDocumentPages(selectedDocument.content).length} {parseDocumentPages(selectedDocument.content).length === 1 ? 'page' : 'pages'}</span>
                                                    </div>
                                                    
                                                    {selectedDocument.file_size && (
                                                        <div className="flex items-center gap-1 text-sm text-gray-500">
                                                            <span>{Math.round(selectedDocument.file_size/1024)} KB</span>
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                {/* Page toggle controls */}
                                                {selectedDocument && parseDocumentPages(selectedDocument.content || '').length > 1 && (
                                                    <div className="mb-4 flex justify-end gap-2">
                                                        <button
                                                            onClick={() => setCollapsedPages(new Set())}
                                                            className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition-colors"
                                                        >
                                                            Expand All Pages
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                const pages = parseDocumentPages(selectedDocument.content || '');
                                                                const allPageKeys = pages.map(p => `${selectedDocument.id}-page-${p.pageNumber}`);
                                                                setCollapsedPages(new Set(allPageKeys));
                                                            }}
                                                            className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                                                        >
                                                            Collapse All Pages
                                                        </button>
                                                    </div>
                                                )}
                                                
                                                {/* Document Content - PDF-like pages */}
                                                <div className="space-y-6">
                                                    {!selectedDocument ? (
                                                        <div className="flex justify-center py-8">
                                                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2" style={{ borderTopColor: 'rgb(250, 204, 21)', borderBottomColor: 'rgb(250, 204, 21)' }}></div>
                                                        </div>
                                                    ) : selectedDocument.parsedContent ? (
                                                        // Handle parsed JSON content with PDF-like pages
                                                        selectedDocument.parsedContent[0]?.pages?.map((page, index) => {
                                                            const pageKey = `${selectedDocument.id}-page-${index + 1}`;
                                                            const isCollapsed = collapsedPages.has(pageKey);
                                                            
                                                            return (
                                                                <div key={pageKey} className="pdf-page bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden toggleable mb-8">
                                                                    {/* Page Header - Clickable */}
                                                                    <div 
                                                                        className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => togglePageCollapse(pageKey)}
                                                                    >
                                                                        <div className="flex items-center gap-3">
                                                                            <ChevronDownIcon 
                                                                                className={`h-5 w-5 text-gray-500 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                                                                            />
                                                                            <span className="text-sm font-medium text-gray-700">Page {index + 1}</span>
                                                                        </div>
                                                                        <span className="text-xs text-gray-400">{selectedDocument.filename}</span>
                                                                    </div>
                                                                    
                                                                    {/* Page Content - Collapsible */}
                                                                    {!isCollapsed && (
                                                                        <>
                                                                            <div className="p-8" style={{ minHeight: '540px' }}>
                                                                                <MarkdownRenderer 
                                                                                    content={cleanDocumentContent(page.markdown)}
                                                                                    colorScheme="yellow"
                                                                                />
                                                                                
                                                                                {/* Render images if any */}
                                                                                {page.images && page.images.length > 0 && (
                                                                                    <div className="mt-6 grid grid-cols-1 gap-4">
                                                                                        {page.images.map((img, imgIndex) => {
                                                                                            const imageUrl = img.url || img;
                                                                                            // Use proxy for external images to avoid CORS issues
                                                                                            const proxiedUrl = imageUrl.startsWith('http') 
                                                                                                ? `/api/knowledge-base/proxy/image?url=${encodeURIComponent(imageUrl)}`
                                                                                                : imageUrl;
                                                                                            
                                                                                            return (
                                                                                                <img 
                                                                                                    key={imgIndex}
                                                                                                    src={proxiedUrl}
                                                                                                    alt={`Image ${imgIndex + 1} on page ${index + 1}`}
                                                                                                    className="max-w-full h-auto rounded-lg shadow-sm border border-gray-200"
                                                                                                    onError={(e) => {
                                                                                                        // Hide image if it fails to load
                                                                                                        e.currentTarget.style.display = 'none';
                                                                                                    }}
                                                                                                    loading="lazy"
                                                                                                />
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            
                                                                            {/* Page Footer */}
                                                                            <div className="bg-gray-50 px-6 py-2 border-t border-gray-200">
                                                                                <div className="text-center text-xs text-gray-400">
                                                                                    Page {index + 1} of {selectedDocument.parsedContent[0]?.pages?.length || 0}
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            );
                                                        })
                                                    ) : selectedDocument.content ? (
                                                        // Handle plain text content with PDF-like pages
                                                        parseDocumentPages(selectedDocument.content).map((page) => {
                                                            const pageKey = `${selectedDocument.id}-page-${page.pageNumber}`;
                                                            const isCollapsed = collapsedPages.has(pageKey);
                                                            
                                                            return (
                                                                <div key={pageKey} className="pdf-page bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden toggleable mb-8">
                                                                    {/* Page Header - Clickable */}
                                                                    <div 
                                                                        className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => togglePageCollapse(pageKey)}
                                                                    >
                                                                        <div className="flex items-center gap-3">
                                                                            <ChevronDownIcon 
                                                                                className={`h-5 w-5 text-gray-500 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                                                                            />
                                                                            <span className="text-sm font-medium text-gray-700">Page {page.pageNumber}</span>
                                                                        </div>
                                                                        <span className="text-xs text-gray-400">{selectedDocument.filename}</span>
                                                                    </div>
                                                                    
                                                                    {/* Page Content - Collapsible */}
                                                                    {!isCollapsed && (
                                                                        <>
                                                                            <div className="p-8" style={{ minHeight: '540px' }}>
                                                                                <MarkdownRenderer 
                                                                                    content={cleanDocumentContent(page.content)}
                                                                                    colorScheme="yellow"
                                                                                />
                                                                            </div>
                                                                            
                                                                            {/* Page Footer */}
                                                                            <div className="bg-gray-50 px-6 py-2 border-t border-gray-200">
                                                                                <div className="text-center text-xs text-gray-400">
                                                                                    Page {page.pageNumber} of {parseDocumentPages(selectedDocument.content).length}
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            );
                                                        })
                                                    ) : (
                                                        // No content available
                                                        <div className="text-center py-8">
                                                            <DocumentIcon className="h-12 w-12 mx-auto mb-3" style={{ color: 'rgb(250, 204, 21, 0.5)' }} />
                                                            <p className="text-gray-500">No content available for this document</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <DocumentIcon className="h-16 w-16 mx-auto mb-4" style={{ color: 'rgb(250, 204, 21, 0.5)' }} />
                                            <h3 className="text-xl font-medium text-gray-800 mb-2">Document Repository</h3>
                                            <p className="text-gray-500 max-w-lg mx-auto">
                                                Select a document from the list to view its contents. 
                                                This section shows all documents uploaded.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'youtube' && (
                        /* YouTube Videos Tab */
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 max-w-7xl mx-auto relative">
                            {/* YouTube Videos Sidebar */}
                            <div className="lg:col-span-1 lg:sticky lg:top-6 lg:self-start">
                                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                                    <div className="p-4 bg-white">
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                                <svg className="h-5 w-5" fill="rgb(250, 204, 21)" viewBox="0 0 24 24">
                                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                                </svg>
                                                YouTube Videos
                                            </h2>
                                            <button
                                                onClick={fetchYouTubeVideos}
                                                className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                                                title="Refresh videos"
                                            >
                                                <ArrowPathIcon className="h-4 w-4 text-gray-500" />
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="p-4">
                                        {youtubeLoading ? (
                                            <div className="py-20 flex justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2" style={{ borderTopColor: 'rgb(250, 204, 21)', borderBottomColor: 'rgb(250, 204, 21)' }}></div>
                                            </div>
                                        ) : youtubeVideos.length === 0 ? (
                                            <div className="text-center py-8">
                                                <svg className="h-12 w-12 mx-auto mb-3" fill="rgba(250, 204, 21, 0.5)" viewBox="0 0 24 24">
                                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                                </svg>
                                                <p className="text-gray-500">No YouTube videos in knowledge base</p>
                                            </div>
                                        ) : (
                                            <>
                                            <nav className="space-y-2 max-h-[calc(100vh-260px)] overflow-y-auto pr-2 custom-scrollbar">
                                                {youtubeVideos.map((video) => (
                                                    <div 
                                                        key={video.id}
                                                        onClick={() => setSelectedVideo(video)}
                                                        className={`flex items-start px-3 py-3 text-sm rounded-md cursor-pointer transition-colors duration-200 ${
                                                            selectedVideo?.id === video.id
                                                            ? 'bg-yellow-50 text-black font-semibold'
                                                            : 'text-gray-700 hover:text-gray-800'
                                                        }`}
                                                    >
                                                        <img 
                                                            src={video.thumbnail_url} 
                                                            alt={video.title}
                                                            className="w-20 h-12 object-cover rounded mr-3 flex-shrink-0"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <span className="block truncate">
                                                                {video.title}
                                                            </span>
                                                            <span className={`text-xs block mt-1 ${
                                                                selectedVideo?.id === video.id ? 'text-black' : 'text-gray-400'
                                                            }`}>
                                                                {video.channel_name}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </nav>
                                            {/* Load more for youtube - outside scroll */}
                                            {youtubeTotalPages > youtubePage && (
                                                <div className="mt-3 flex justify-center">
                                                    <button
                                                        onClick={loadMoreYouTube}
                                                        disabled={youtubeLoading}
                                                        className="px-3 py-2 text-sm rounded bg-yellow-100 text-yellow-800 hover:bg-yellow-200 disabled:opacity-60"
                                                    >
                                                        {youtubeLoading ? 'Loading...' : 'Load more'}
                                                    </button>
                                                </div>
                                            )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* YouTube Video Content */}
                            <div className="lg:col-span-3 mb-20 lg:mb-0">
                                <div className="bg-white rounded-xl shadow-sm p-6 min-h-[calc(100vh-200px)]">
                                    {selectedVideo ? (
                                        <div>
                                            
                                            <div>
                                                {/* Video Title */}
                                                <h2 className="text-2xl font-bold text-gray-900 mb-4">{selectedVideo.title}</h2>
                                                
                                                {/* Video Meta Info */}
                                                <div className="flex flex-wrap gap-4 mb-6">
                                                    <div className="flex items-center gap-1 text-sm text-gray-500">
                                                        <svg className="h-4 w-4" fill="rgb(250, 204, 21)" viewBox="0 0 24 24">
                                                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                                        </svg>
                                                        <span>{selectedVideo.channel_name}</span>
                                                    </div>
                                                    
                                                    <div className="flex items-center gap-1 text-sm text-gray-500">
                                                        <ClockIcon className="h-4 w-4" style={{ color: 'rgb(250, 204, 21)' }} />
                                                        <span>Added: {new Date(selectedVideo.created_at).toLocaleDateString()}</span>
                                                    </div>
                                                    
                                                    <a 
                                                        href={selectedVideo.video_url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                                                    >
                                                        <LinkIcon className="h-4 w-4" />
                                                        <span>Watch on YouTube</span>
                                                    </a>
                                                </div>
                                                
                                                {/* Video Embed */}
                                                <div className="mb-8">
                                                    <div className="relative pb-[56.25%] h-0">
                                                        <iframe
                                                            className="absolute top-0 left-0 w-full h-full rounded-lg"
                                                            src={`https://www.youtube.com/embed/${selectedVideo.video_id}`}
                                                            title={selectedVideo.title}
                                                            frameBorder="0"
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                            allowFullScreen
                                                        />
                                                    </div>
                                                </div>
                                                
                                                {/* Video Transcript */}
                                                <VideoTranscript video={selectedVideo} clientId={clientId} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <svg className="h-16 w-16 mx-auto mb-4" fill="rgba(250, 204, 21, 0.5)" viewBox="0 0 24 24">
                                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                            </svg>
                                            <h3 className="text-xl font-medium text-gray-800 mb-2">YouTube Video Library</h3>
                                            <p className="text-gray-500 max-w-lg mx-auto">
                                                Select a video from the list to view its transcript. 
                                                These videos have been added to your knowledge base.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'products' && (
                        /* Products Tab */
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 max-w-7xl mx-auto relative">
                            {/* Categories Sidebar */}
                            <div className="lg:col-span-1 lg:sticky lg:top-6 lg:self-start">
                                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                                    <div className="p-4 bg-white border-b border-gray-200">
                                        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                            <TagIcon className="h-5 w-5 text-orange-500" />
                                            Categories
                                        </h2>
                                    </div>

                                    <div className="p-4">
                                        <nav className="space-y-1 max-h-[calc(100vh-260px)] overflow-y-auto pr-2 custom-scrollbar">
                                            <button
                                                onClick={() => handleCategoryClick(null)}
                                                className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors duration-200 ${
                                                    selectedCategory === null
                                                    ? 'bg-orange-50 text-orange-600 font-semibold'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                                }`}
                                            >
                                                All Products
                                            </button>

                                            {categoryTree && categoryTree.length > 0 ? (
                                                categoryTree.map(node => renderCategoryNode(node))
                                            ) : (
                                                productsCategories.length === 0 ? (
                                                    <div className="text-center py-8">
                                                        <TagIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                                        <p className="text-gray-500 text-sm">No categories found</p>
                                                    </div>
                                                ) : (
                                                    productsCategories.map((category) => (
                                                        <button
                                                            key={category.slug}
                                                            onClick={() => handleCategoryClick(category.name)}
                                                            className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors duration-200 ${
                                                                selectedCategory === category.name
                                                                ? 'bg-orange-50 text-orange-600 font-semibold'
                                                                : 'text-gray-700 hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <span className="truncate">{category.name}</span>
                                                                <span className="ml-2 text-xs text-gray-400">{category.count}</span>
                                                            </div>
                                                        </button>
                                                    ))
                                                )
                                            )}
                                        </nav>
                                    </div>
                                </div>
                            </div>

                            {/* Products Grid */}
                            <div className="lg:col-span-3 mb-20 lg:mb-0">
                                <div className="bg-white rounded-xl shadow-sm min-h-[400px]">
                                    {productsLoading ? (
                                        <div className="flex items-center justify-center py-20">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                                        </div>
                                    ) : products.length === 0 ? (
                                        <div className="flex items-center justify-center py-20">
                                            <div className="text-center">
                                                <TagIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                                <h3 className="text-xl font-medium text-gray-800 mb-2">No Products Found</h3>
                                                <p className="text-gray-500 max-w-lg mx-auto">
                                                    No products have been extracted from your knowledge base yet.
                                                    Products are automatically detected during website crawling.
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-6">
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                {products.map((product) => (
                                                    <div
                                                        key={product.id}
                                                        onClick={() => handleProductClick(product)}
                                                        className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                                                    >
                                                        {/* Product Image */}
                                                {(() => {
                                                    const raw = Array.isArray(product?.metadata?.images) ? product.metadata.images : [];
                                                    const blocked = badImagesByUrl[product.url] || new Set<string>();
                                                    const imgs = raw
                                                        .map((img: any) => (typeof img === 'string' ? img : (img?.url || '')))
                                                        .filter((src: string) => src && !isPlaceholderImage(src) && !blocked.has(src));
                                                    const total = imgs.length;
                                                    if (total > 0) {
                                                        const idx = carouselIndexByUrl[product.url] || 0;
                                                        const safeIdx = Math.min(Math.max(idx, 0), total - 1);
                                                        const imgUrl = toProxied(imgs[safeIdx]);
                                                        return (
                                                            <div className="relative h-48 bg-gray-100">
                                                                <img
                                                                    src={imgUrl}
                                                                    alt={product.title}
                                                                    className="w-full h-full object-cover"
                                                                    decoding="async"
                                                                    referrerPolicy="no-referrer"
                                                                    onError={() => handleImageError(product.url, imgs[safeIdx], total)}
                                                                    loading="lazy"
                                                                />
                                                                {total > 1 && (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => { e.stopPropagation(); prevImage(product.url, total); }}
                                                                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white text-gray-700 rounded-full w-8 h-8 flex items-center justify-center shadow"
                                                                            aria-label="Previous image"
                                                                        >
                                                                            ‹
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => { e.stopPropagation(); nextImage(product.url, total); }}
                                                                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white text-gray-700 rounded-full w-8 h-8 flex items-center justify-center shadow"
                                                                            aria-label="Next image"
                                                                        >
                                                                            ›
                                                                        </button>
                                                                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                                                                            {imgs.map((_, dotIdx) => (
                                                                                <span key={dotIdx} className={`h-1.5 w-1.5 rounded-full ${dotIdx === safeIdx ? 'bg-white' : 'bg-white/60'}`} />
                                                                            ))}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                    return (
                                                            <div className="h-48 bg-gray-100 flex items-center justify-center">
                                                                <svg className="h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                            </div>
                                                    );
                                                })()}

                                                        {/* Product Info */}
                                                        <div className="p-4">
                                                            <h3 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">
                                                                {product.url ? (
                                                                    <a
                                                                        href={product.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="hover:text-orange-600 transition-colors duration-150"
                                                                        title={product.title || product.url}
                                                                    >
                                                                        {product.title || product.url}
                                                                    </a>
                                                                ) : (
                                                                    product.title
                                                                )}
                                                            </h3>

                                                            {/* Price */}
                                                            {product.metadata?.price && (
                                                                <p className="text-orange-600 font-semibold text-lg mb-2">
                                                                    {product.metadata.currency || 'PKR'} {product.metadata.price}
                                                                </p>
                                                            )}

                                                            {/* Category */}
                                                            {product.category && (
                                                                <p className="text-xs text-gray-500 truncate">
                                                                    {product.category}
                                                                </p>
                                                            )}

                                                            {/* Availability */}
                                                            {product.metadata?.availability && (
                                                                <span className={`inline-block mt-2 px-2 py-1 text-xs rounded ${
                                                                    product.metadata.availability === 'in_stock'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-red-100 text-red-800'
                                                                }`}>
                                                                    {product.metadata.availability === 'in_stock' ? 'In Stock' : 'Out of Stock'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title="Delete Document"
                message={`Are you sure you want to delete "${deleteModal.documentName}"? This action cannot be undone and will remove all associated data from the knowledge base.`}
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
                isLoading={isDeleting}
            />
            
            {/* Clean Knowledge Base Confirmation Modal */}
            <ConfirmationModal
                isOpen={cleanKBModal.isOpen}
                onClose={handleCleanKBCancel}
                onConfirm={handleCleanKBConfirm}
                title="Clean Knowledge Base"
                message="Are you sure you want to clean the entire knowledge base? This will permanently delete:\n• All uploaded documents\n• All YouTube video data\n• All scraped website content\n• All embeddings from Pinecone\n\nThis action cannot be undone."
                confirmText="Clean Everything"
                cancelText="Cancel"
                confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
                isLoading={isCleaning}
            />
        </div>
    );
};

// Add this right before the export statement
// CSS for custom scrollbar
const style = document.createElement('style');
style.textContent = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f9f9f9;
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(250, 204, 21, 0.6);
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgb(250, 204, 21);
  }
  
  /* PDF-like page styling */
  .pdf-page {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 6px 20px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.3s ease;
  }
  
  .pdf-page:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15), 0 12px 40px rgba(0, 0, 0, 0.1);
  }
  
  /* Print-like appearance */
  .pdf-page {
    background: linear-gradient(135deg, #ffffff 0%, #fefefe 100%);
  }
`;
document.head.appendChild(style);

export default KnowledgeBase; 
